import { useState } from 'react';
import { Loader2, ShieldQuestion } from 'lucide-react';

import { publicApi, ApiError } from '../lib/api.js';

/**
 * The security question.
 *
 * Nothing about the invoice is on this screen — not the amount, not the
 * customer's name. A forwarded email must not disclose what was invoiced or to
 * whom, so the page that stands in front of it cannot either. That is also why
 * the prompt says "the total shown on your invoice" rather than showing it.
 */
export function Verify({
  token,
  kind,
  attemptsRemaining,
  onVerified,
  onDead,
}: {
  token: string;
  kind: 'total' | 'zip';
  attemptsRemaining: number;
  onVerified: () => Promise<void>;
  onDead: (message: string) => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(attemptsRemaining);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await publicApi.post(`/api/pay/${token}/verify`, { value });
      await onVerified();
    } catch (e) {
      if (e instanceof ApiError) {
        // 403 here means the link locked or died — there is nothing further
        // this screen can offer.
        if (e.status === 403 || e.status === 404) {
          onDead(e.message);
          return;
        }
        setError(e.message);
        setRemaining((previous) => Math.max(0, previous - 1));
      } else {
        setError('Something went wrong. Try again.');
      }
      setValue('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card mx-auto max-w-md p-6">
      <div className="flex items-start gap-3">
        <ShieldQuestion className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" aria-hidden />
        <div>
          <h1 className="font-semibold text-slate-900">Confirm it's you</h1>
          <p className="mt-1 text-sm text-slate-600">
            Before we show the invoice, please confirm{' '}
            {kind === 'total'
              ? 'the total amount shown on it.'
              : 'the ZIP code on your billing address.'}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <label className="field-label" htmlFor="answer">
            {kind === 'total' ? 'Invoice total' : 'Billing ZIP code'}
          </label>
          <input
            id="answer"
            className={`field-input font-account text-lg ${error ? 'field-input-error' : ''}`}
            inputMode={kind === 'total' ? 'decimal' : 'numeric'}
            placeholder={kind === 'total' ? '0.00' : '46565'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            required
            autoComplete="off"
          />
          {kind === 'total' && (
            <p className="mt-1 text-xs text-slate-500">
              Dollars and cents. A dollar sign and commas are fine.
            </p>
          )}
        </div>

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={busy || value.trim() === ''}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Continue
        </button>

        {remaining <= 2 && remaining > 0 && (
          <p className="text-center text-xs text-amber-800">
            {remaining} attempt{remaining === 1 ? '' : 's'} left before this link locks.
          </p>
        )}
      </form>

      <p className="mt-5 border-t border-slate-100 pt-4 text-xs text-slate-500">
        Can't work it out? Reply to the email we sent, or call us on 574-555-0100 and we will read
        it to you.
      </p>
    </div>
  );
}
